import React from 'react';
import { Flex, Text, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { KeyMetricsNumberFormatter } from 'src/utils/TickFormatters';
import { UpDownIndicator } from '../../atoms/UpDownIndicator';

type MMKeyMetricItemProps = {
  title: string;
  icon: React.ReactNode;
  value: number;
  upOrDown: 'up' | 'down';
  upOrDownValue: string;
};

const tooltipText =
  'Shows how much the latest value has increased or decreased compared to the first value within the selected date range.';

export const KeyMetricItem: React.FC<MMKeyMetricItemProps> = ({
  title,
  icon,
  value,
  upOrDown,
  upOrDownValue,
}) => {
  const valueColor = useColorModeValue(
    'text.darkBlue.900',
    'text.dark.highContrast',
  );

  return (
    <Flex
      borderWidth="thin"
      borderColor="border.light.primary"
      bg="surface.card"
      p={{ base: 1, sm: 2, md: 4 }}
      align="center"
      justifyContent="space-between"
      borderRadius="xl">
      <Flex direction="column" gap={{ base: 1, md: 2, lg: 2 }}>
        <Text
          fontSize={{ base: 'xs', md: 'sm', lg: 'sm' }}
          fontWeight="semibold"
          color="text.darkGray"
          fontFamily="marketingPage">
          {title}
        </Text>
        <Flex alignItems="center" gap={{ base: 1, sm: 1, md: 2, lg: 2 }}>
          <Text
            fontSize={{ base: 'md', md: 'lg', lg: 'xl' }}
            fontWeight="bold"
            color={valueColor}
            fontFamily="marketingPage">
            {KeyMetricsNumberFormatter(value)[0]}
          </Text>
          <Tooltip label={tooltipText}>
            <Flex alignItems="center" gap={1} cursor="pointer">
              <UpDownIndicator upOrDown={upOrDown} />
              <Text
                color={upOrDown === 'up' ? 'icon.green' : 'icon.red'}
                fontSize="sm"
                fontWeight="semibold">
                {upOrDownValue}
              </Text>
            </Flex>
          </Tooltip>
        </Flex>
      </Flex>
      <Flex
        bg="surface.control"
        p={2}
        alignItems="center"
        height={{ base: '16px', md: '24px', lg: '40px', xl: '50px' }}
        borderRadius="8px"
        width={{ base: '16px', md: '24px', lg: '40px', xl: '50px' }}
        justifyContent="center">
        {icon}
      </Flex>
    </Flex>
  );
};
